import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TokenService } from './token.service';

import { CURRENCY_ENDPOINTS } from '../api/api-endpoints';
import { RestApiService } from '../api/rest-api.service';
import { AutocompleteOption } from '../../shared/inputs/autocomplete-select/autocomplete-select.component';

export interface CurrencyOption extends AutocompleteOption {
  id: string;
  value: string;
  name: string;
  code: string;
  symbol: string;
  decimal_places: number;
  is_base: boolean;
  active: boolean;
}

export interface CurrencyConversion {
  source_currency_id: string;
  target_currency_id: string;
  source_amount: number;
  converted_amount: number;
  rate: number;
  effective_date: string | null;
  source: string;
}

@Injectable({ providedIn: 'root' })
export class CurrencyService {
  private readonly optionRequests = new Map<string, Promise<CurrencyOption[]>>();
  private readonly historicalRequests = new Map<string, Promise<CurrencyOption[]>>();
  private readonly countryDefaults = new Map<string, Promise<CurrencyOption>>();

  private cacheSession: string | null = null;

  constructor(private readonly api: RestApiService, private readonly tokens: TokenService) {}

  private checkSession(): void {
    const session = this.tokens.getAccessToken();
    if (session !== this.cacheSession) {
      this.optionRequests.clear();
      this.historicalRequests.clear();
      this.cacheSession = session;
    }
  }

  catalogue(query = ''): Promise<CurrencyOption[]> {
    return this.loadOptions(query, true);
  }

  async setActive(code: string, active: boolean): Promise<CurrencyOption> {
    const result = await firstValueFrom(this.api.put<CurrencyOption>(
      CURRENCY_ENDPOINTS.activation(normalizeCode(code)), { active },
    ));
    this.optionRequests.clear();
    this.historicalRequests.clear();
    return result;
  }

  options(query = ''): Promise<CurrencyOption[]> {
    return this.loadOptions(query, false);
  }

  async organisationDefaultCode(): Promise<string> {
    const base = (await this.options()).find(option => option.is_base && option.active);
    if (!base) throw new Error('The organisation base currency could not be loaded.');
    return base.code;
  }

  private loadOptions(query: string, includeInactive: boolean): Promise<CurrencyOption[]> {
    this.checkSession();
    const search = query.trim();
    const key = `${includeInactive}:${search.toLowerCase()}`;
    const cached = this.optionRequests.get(key);
    if (cached) return cached.then(cloneOptions);

    const params = new URLSearchParams({ limit: '250' });
    if (search) params.set('query', search);
    if (includeInactive) params.set('include_inactive', 'true');
    const request = firstValueFrom(
      this.api.get<{ options?: CurrencyOption[] }>(
        `${CURRENCY_ENDPOINTS.options}?${params.toString()}`,
      ),
    ).then((response) => Array.isArray(response.options) ? response.options : [])
      .catch((error) => {
        this.optionRequests.delete(key);
        throw error;
      });
    this.optionRequests.set(key, request);
    return request.then(cloneOptions);
  }

  preload(values: string[]): Promise<CurrencyOption[]> {
    this.checkSession();
    const requested = [...new Set(values.map(normalizeCode).filter(Boolean))].sort();
    if (requested.length === 0) return Promise.resolve([]);
    const key = requested.join(',');
    const cached = this.historicalRequests.get(key);
    if (cached) return cached.then(cloneOptions);
    const request = firstValueFrom(this.api.get<{ options: CurrencyOption[] }>(
      `${CURRENCY_ENDPOINTS.options}?${new URLSearchParams({ codes: key, limit: '250' })}`,
    )).then((response) => response.options.map((option) => ({ ...option, disabled: !option.active })))
      .catch((error) => { this.historicalRequests.delete(key); throw error; });
    this.historicalRequests.set(key, request);
    return request.then(cloneOptions);
  }

  defaultForCountry(countryIso: string): Promise<CurrencyOption> {
    const country = countryIso.trim().toUpperCase();
    const cached = this.countryDefaults.get(country);
    if (cached) return cached.then((option) => ({ ...option }));

    const request = firstValueFrom(
      this.api.get<CurrencyOption>(
        `${CURRENCY_ENDPOINTS.defaultForCountry}?${new URLSearchParams({ country }).toString()}`,
      ),
    ).catch((error) => {
      this.countryDefaults.delete(country);
      throw error;
    });
    this.countryDefaults.set(country, request);
    return request.then((option) => ({ ...option }));
  }

  convert(sourceCurrency: string, targetCurrency: string, amount: number): Promise<CurrencyConversion> {
    return firstValueFrom(this.api.post<CurrencyConversion>(CURRENCY_ENDPOINTS.convert, {
      source_currency_id: normalizeCode(sourceCurrency),
      target_currency_id: normalizeCode(targetCurrency),
      amount,
    }));
  }
}

function normalizeCode(value: string): string {
  return String(value || '').trim().toUpperCase();
}

function cloneOptions(options: CurrencyOption[]): CurrencyOption[] {
  return options.map((option) => ({ ...option }));
}
